import { computed, nextTick, onMounted, onUnmounted, toRef } from 'vue';
import { useI18n } from '@/composables';
import { useFlowPathFieldErrors } from '@/composables/useFlowDialogFieldErrors';
import { $emitter } from '@/services/emitter';

type FlowPathItem = {
  name?: string;
  weight: number;
  status?: string;
  landings: Array<{ id: string; weight?: number }>;
  offers: Array<{ id: string; weight: number }>;
};

type FlowRule = {
  default?: boolean;
  paths: FlowPathItem[];
};

type FlowPathSettingsForm = {
  workspaceId: string;
  rules: Array<{
    paths: Array<{
      landings: Array<{ id: string; weight?: number }>;
      offers: Array<{ id: string; weight: number }>;
    }>;
  }>;
};

type FlowPathSettingsProps = {
  rule: FlowRule;
  ruleIndex: number;
  pathIndex: number;
  path: FlowPathItem;
  form: FlowPathSettingsForm;
  offerOptions: Offer[];
  landingOptions: Landing[];
  errorsRaw: unknown[][] | null;
};

export const useFlowPathSettings = (
  props: FlowPathSettingsProps,
  emit: (event: 'change', status: string | undefined) => void,
) => {
  const { t } = useI18n();

  const errorCondition = computed(() => [
    'rules',
    props.ruleIndex + 1,
    'paths',
    props.pathIndex + 1,
  ]);

  const {
    isEmptyLandingsError,
    isEmptyOffersError,
    nameErrorMessages,
    weightErrorMessages,
  } = useFlowPathFieldErrors({
    errorsRaw: toRef(props, 'errorsRaw'),
    errorCondition,
  });

  const activeSwitchDisabled = computed(() => {
    const weight = Number(props.path.weight);
    return props.path.weight <= 0 || Number.isNaN(weight);
  });

  const pathActive = computed({
    get: () => (!activeSwitchDisabled.value ? props.path.status === 'active' : false),
    set: (value: boolean) => {
      emit('change', props.path.status);

      if (value) {
        props.path.status = 'active';
      } else {
        props.path.status = 'inactive';
      }
    },
  });

  const showDelete = computed(() => {
    if (!props.rule.default) {
      return true;
    }

    return props.rule.paths.length > 1;
  });

  const addNewOffer = (offer: Offer) => {
    props.path.offers.push({
      ...offer,
      weight: 100,
    });
  };

  const updateOffer = (updatedOffer: Offer) => {
    const index = props.offerOptions.findIndex((item) => item.id === updatedOffer.id);
    if (index !== -1) {
      props.offerOptions[index] = updatedOffer;
    }

    props.form.rules.forEach((rule, ruleIndex) => {
      rule.paths.forEach((path, pathIndex) => {
        path.offers.forEach((offer, offerIndex) => {
          if (offer.id === updatedOffer.id) {
            props.form.rules[ruleIndex].paths[pathIndex].offers[offerIndex] = {
              ...offer,
              ...updatedOffer,
            };
          }
        });
      });
    });
  };

  const addNewLanding = (landing: Landing) => {
    props.path.landings.push({
      ...landing,
      weight: 100,
    });
  };

  const updateLanding = (updatedLanding: Landing) => {
    const index = props.landingOptions.findIndex((item) => item.id === updatedLanding.id);
    if (index !== -1) {
      props.landingOptions[index] = updatedLanding;
    }

    props.form.rules.forEach((rule, ruleIndex) => {
      rule.paths.forEach((path, pathIndex) => {
        path.landings.forEach((landing, landingIndex) => {
          if (landing.id === updatedLanding.id) {
            props.form.rules[ruleIndex].paths[pathIndex].landings[landingIndex] = {
              ...landing,
              ...updatedLanding,
            };
          }
        });
      });
    });
  };

  const onBusFlowNewLanding = () => {
    nextTick(() => {
      $emitter.emit('open-dialog', {
        name: 'LandingDialog',
        data: { mode: 'create', workspaceId: props.form.workspaceId, onClose: addNewLanding },
      });
    });
  };

  const onBusFlowEditLanding = (id: string) => {
    nextTick(() => {
      $emitter.emit('open-dialog', {
        name: 'LandingDialog',
        data: { mode: 'edit', id, onEdit: updateLanding },
      });
    });
  };

  const onBusFlowNewOffer = () => {
    nextTick(() => {
      $emitter.emit('open-dialog', {
        name: 'OfferDialog',
        data: { mode: 'create', onClose: addNewOffer },
      });
    });
  };

  const onBusFlowEditOffer = (id: string) => {
    nextTick(() => {
      $emitter.emit('open-dialog', {
        name: 'OfferDialog',
        data: { mode: 'edit', id, onEdit: updateOffer },
      });
    });
  };

  onMounted(() => {
    $emitter.on('flow-new-landing', onBusFlowNewLanding);
    $emitter.on('flow-edit-landing', onBusFlowEditLanding);
    $emitter.on('flow-new-offer', onBusFlowNewOffer);
    $emitter.on('flow-edit-offer', onBusFlowEditOffer);
  });

  onUnmounted(() => {
    $emitter.off('flow-new-landing', onBusFlowNewLanding);
    $emitter.off('flow-edit-landing', onBusFlowEditLanding);
    $emitter.off('flow-new-offer', onBusFlowNewOffer);
    $emitter.off('flow-edit-offer', onBusFlowEditOffer);
  });

  return {
    t,
    pathActive,
    activeSwitchDisabled,
    showDelete,
    isEmptyLandingsError,
    isEmptyOffersError,
    nameErrorMessages,
    weightErrorMessages,
  };
};
